import appNotificationService from "./utils/appNotificationService";
import {useAuthStore} from "../stores/authStore";
import AxiosService from "./axiosService";
import EventBus from "./utils/appEventBusService";
import BusEnum from "../enums/BusEnum";
import authHeader from "./authHeader";
import WebSocketService from "./webSocketService";

/** Mode d'utilisation, enregistré avec la connexion : APK (Cordova), PWA installée ou web */
function appMode() {
  if (window.cordova) return 'apk'
  // minimal-ui : mode préféré du manifeste (boutons retour et rafraîchir), standalone en repli
  const standalone = window.matchMedia && window.matchMedia('(display-mode: minimal-ui), (display-mode: standalone)').matches
  return standalone || window.navigator.standalone ? 'pwa' : 'web'
}

/**
 * Informations sur l'appareil, enregistrées avec la connexion. Les « Client Hints »
 * (Chrome, Edge) donnent le vrai système (Windows 11, version d'Android) et le
 * modèle, que le User-Agent masque ; Safari et Firefox ne les fournissent pas.
 */
async function clientInfo() {
  const info = {
    appMode: appMode(),
    screen: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    appVersion: process.env.APP_VERSION,
    network: navigator.connection ? (navigator.connection.type && navigator.connection.type !== 'unknown'
      ? navigator.connection.type : navigator.connection.effectiveType) : undefined
  }
  try {
    const uaData = navigator.userAgentData
    if (uaData && uaData.getHighEntropyValues) {
      // Délai court : la connexion ne doit pas attendre le navigateur
      const hints = await Promise.race([
        uaData.getHighEntropyValues(['platformVersion', 'model']),
        new Promise((resolve) => setTimeout(() => resolve(null), 500))
      ])
      info.platform = uaData.platform
      info.mobile = uaData.mobile
      if (hints) {
        info.platformVersion = hints.platformVersion
        info.model = hints.model
      }
    }
  } catch (e) {
    // Informations facultatives
  }
  return info
}

class AuthService {

  getStoreInstance() {
    if(!this.store) {
      this.store = useAuthStore()
      EventBus.on(BusEnum.AUTH_UPDATE, data => {
        // Diffusé à tous (outils de prix changés par un admin) : seul l'utilisateur concerné, s'il est connecté, recharge
        if(this.store.currentUser && this.store.currentUser.id===data.message.id) {
          this.refreshLogin().then((user) => {
            appNotificationService.pushInfo("Current user updated")
          })
        }
      })
    }
    return this.store
  }

  async refreshLogin() {
    try {
      let response = await AxiosService.get( "/auth/refreshLogin", {
        headers: authHeader()
      })
      if (response.data.accessToken) {
        await this.getStoreInstance().login(response.data)
        // Socket WebSocket rouverte avec le jeton : notifications et diffusions destinées à l'utilisateur
        WebSocketService.getInstance().reconnect()
      }
      return response.data;
    } catch (error) {
      appNotificationService.pushError(error.message);
      throw error;
    }
  }

  async login(user) {
    try {
      let response = await AxiosService.post( "/auth/signin", {
        username: user.username,
        password: user.password,
        client: await clientInfo()
      })
      if (response.data.accessToken) {
        await this.getStoreInstance().login(response.data)
        // Socket WebSocket rouverte avec le jeton : notifications et diffusions destinées à l'utilisateur
        WebSocketService.getInstance().reconnect()
      }
      return response.data;
    } catch (error) {
      // Message du serveur (identifiant ou mot de passe incorrect) plutôt que « Request failed with status code 401 »
      error.message = error.response?.data?.message || error.message
      throw error;
    }
  }

  logout() {
    localStorage.removeItem("user");
    this.getStoreInstance().logout()
  }

  register(user) {
    return AxiosService.post( "/signup", {
      username: user.username,
      email: user.email,
      password: user.password
    });
  }
}

export {clientInfo};
export default new AuthService();
